import { useState, useEffect, useCallback } from 'react';
import { axiosInstance as axios } from '../context/AuthContext';

const API = '/api';

/**
 * Hook to fetch and manage user permissions for a family
 * 
 * Permissions are based on:
 * - Owner status (has all permissions)
 * - Assigned roles (Vision Keeper, Scribe, Harvest Guardian, etc.)
 * 
 * Available permissions:
 * - owner, transfer_ownership, archive_family, edit_family, remove_members
 * - Vision Keeper: edit_family_description, manage_goals, pin_announcements
 * - Scribe: manage_stories, edit_timeline, edit_others_stories
 * - Harvest Guardian: manage_dues, view_payments, record_payments, charge_dues, allocate_funds
 * - Bridge Builder: send_priority_messages, moderate_chat, create_reconciliation_events
 * - Legacy Anchor: manage_spiritual, manage_traditions, manage_gratitude
 * - Circle Steward: manage_events, send_invitations, manage_rsvps, assign_tasks, set_budgets
 * - Archivist: manage_gallery, upload_photos, create_share_links, edit_captions
 */
export function usePermissions(familyId) {
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = useCallback(async () => {
    if (!familyId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/families/${familyId}/my-permissions`, {
        withCredentials: true
      });
      
      setPermissions(response.data.permissions || []);
      setRoles(response.data.roles || []);
      setIsOwner(response.data.is_owner || false);
      setError(null);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err);
      // Default to no permissions on error
      setPermissions([]);
      setRoles([]);
      setIsOwner(false);
    } finally {
      setLoading(false);
    }
  }, [familyId]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission) => {
    // Owner has all permissions
    if (isOwner) return true;
    return permissions.includes(permission);
  }, [permissions, isOwner]);

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (isOwner) return true;
    return permissionList.some(p => permissions.includes(p));
  }, [permissions, isOwner]);

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback((permissionList) => {
    if (isOwner) return true;
    return permissionList.every(p => permissions.includes(p));
  }, [permissions, isOwner]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((roleTitle) => {
    return roles.includes(roleTitle);
  }, [roles]);

  return {
    permissions,
    roles,
    isOwner,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    refetch: fetchPermissions
  };
}

export default usePermissions;
